package ui.campaign.detailv2.preview

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import api.CampaignState
import api.ResourceType
import common.frontend.api.Io
import org.ssoft.common.type.ScreenRatio
import playlist.ImplPlaylist
import provider.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import ui.GlobalVM
import ui.campaign.detailv2.widgets.ImplCampaignInfoCard
import kotlin.time.Duration.Companion.milliseconds

class CampaignPreviewScope(
    val vm: CampaignPreviewVM,
    val vmGlobal: GlobalVM,
    val action: (CampaignPreviewVM.Action) -> Unit,

    val args: CampaignPreviewArgs,
    val nav: Navigation,
    val login: Io.User,
    val display: CommonDisplayProvider,
    val externals: CommonExternalsProvider
) {

    init {
        vm.init(this)
    }

    companion object {
        @Composable
        fun rememberScope(): CampaignPreviewScope {

            val account = LocalAccountProvider.current.current!!
            val nav = LocalNavigation.current
            val display = LocalDisplayProvider.current
            val externals = LocalExternalsProvider.current
            val args = LocalNavigationArgs.current.rememberArgs(CampaignPreviewArgs::class)
            val vm = nav.viewModel(CampaignPreviewVM::class, "${args.companyId}:${args.campaignGroupId}")
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = CampaignPreviewVM.Action.rememberAction(vm)

            return remember {
                CampaignPreviewScope(
                    vm = vm,
                    vmGlobal = vmGlobal,
                    action = action,
                    args = args,
                    nav = nav,
                    login = account,
                    display = display,
                    externals = externals
                )
            }
        }
    }

    class Wrapper(
        val canAdd: Boolean,
        val canEdit: Boolean,
        val canDelete: Boolean,
        val canCreate: Boolean,
        val data: Io.Campaign,
        val state: CampaignState = CampaignState[data.state],
        val visualsPrimary: List<ImplCampaignInfoCard.Visual> = data.buildVisualsPrimary(),
        val locations: List<ImplCampaignInfoCard.Location> = data.buildLocations(),
        val visualsDynamic: List<ImplCampaignInfoCard.VisualDynamic> = data.buildVisualsDynamic(),
        val applications: List<ImplPlaylist.AppConfig> = data.apps
    ) {
        private companion object {
            fun Io.Campaign.buildVisualsPrimary(): List<ImplCampaignInfoCard.Visual> {
                return visuals.firstOrNull { it.isPrimary }
                    ?.res
                    ?.map { it.toVisual() }
                    ?: listOf()
            }

            private fun Io.Campaign.ResItem.toVisual(): ImplCampaignInfoCard.Visual {
                return ImplCampaignInfoCard.Visual(
                    id = resourceId,
                    width = width,
                    height = height,
                    duration = duration.milliseconds,
                    type = ResourceType[type],
                    ratio = ScreenRatio[screenRatio]
                )
            }

            fun Io.Campaign.buildVisualsDynamic(): List<ImplCampaignInfoCard.VisualDynamic> {
                return visuals.filterNot { it.isPrimary }
                    .map { group ->
                        ImplCampaignInfoCard.VisualDynamic(
                            conditions = group.conditions,
                            weatherPictogramVariants = weatherPictograms,
                            visuals = group.res.map { it.toVisual() }
                        )
                    }.filter { it.visuals.isNotEmpty() }
            }

            fun Io.Campaign.buildLocations(): List<ImplCampaignInfoCard.Location> {
                return locations.filter { it.devices.any { it.isActive } }
                    .map { location ->
                        ImplCampaignInfoCard.Location(
                            id = location.id,
                            name = location.name,
                            devices = location.devices
                                .filter { it.isActive }
                                .map { device ->
                                    ImplCampaignInfoCard.Device(
                                        id = device.id,
                                        name = device.name,
                                        countUsableDays = 0,
                                        impressionSum = 0
                                    )
                                }
                        )
                    }
            }
        }
    }
}

